import { AfterViewInit, Component, computed, ElementRef, inject, Input, signal, ViewChild, WritableSignal } from '@angular/core';
import { TranslateModule, TranslateService } from "@ngx-translate/core";
import { NonNullableFormBuilder, ReactiveFormsModule, ValidationErrors, Validators } from "@angular/forms";
import { ApiService } from "../../api/services/api.service";
import { Wallet, WalletWithRentalDto } from "../../api/models/fraud.models";
import { debounceTime, merge, take } from "rxjs";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import { MatError, MatFormField } from "@angular/material/form-field";
import { MatInput } from "@angular/material/input";
import { MatButton } from "@angular/material/button";
import { Router } from "@angular/router";
import { CdkTextareaAutosize } from "@angular/cdk/text-field";
import { JsonPipe, NgClass, NgOptimizedImage, NgStyle } from "@angular/common";
import { contactAsBot } from "../../partials/variables";
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatIcon } from '@angular/material/icon';
import { InfoComponent } from '../../partials/components/info/info.component';
import { MatRipple } from "@angular/material/core";
import { MAT_DIALOG_DATA, MatDialogRef } from "@angular/material/dialog";
import { addressValidator, tronAddressValidator } from "../add-wallet/add-wallet.component";
import { VariablesService } from '../../partials/services/variables.service';
import { ProjectSettingsService } from '../../partials/services/project-settings.service';
import { DialogService } from '../../partials/services/dialog.service';

// TODO: В будущем переделать на страницу, но учитывать что когда будет переход обратно на страницу subscribed-wallet
// был скрол к кошельку который меняли
@Component({
    selector: 'app-update-wallet',
    imports: [
        TranslateModule,
        ReactiveFormsModule,
        MatTooltipModule,
        MatIcon,
        MatRipple,
        NgClass
    ],
    templateUrl: './update-wallet.component.html',
    styleUrl: './update-wallet.component.scss',
    host: {
        class: 'py-4 flex flex-col gap-3'
    }
})
export default class UpdateWalletComponent implements AfterViewInit {
  @ViewChild('addressInput') addressInput!: ElementRef;

  @Input({ required: true })
  public readonly subscribedWallets: WalletWithRentalDto[] = [];

  public addressErrorMessage: WritableSignal<string> = signal('');

  private readonly data: { wallet: Wallet } = inject<{ wallet: Wallet }>(MAT_DIALOG_DATA);

  public readonly matDialogRef = inject(MatDialogRef);

  private readonly translateService: TranslateService = inject(TranslateService);
  public readonly projectSettingsService: ProjectSettingsService = inject(ProjectSettingsService);

  readonly isAmlEnergyActive = computed(() => this.projectSettingsService.settings()?.isAmlEnergyActive ?? false);

  private readonly _fb: NonNullableFormBuilder = inject(NonNullableFormBuilder);
  readonly variablesService: VariablesService = inject(VariablesService);

  private readonly _apiService: ApiService = inject(ApiService);

  public readonly form = this._fb.group({
    address: this._fb.control('', [
        Validators.required,
        tronAddressValidator(),
        (control): ValidationErrors | null => {
          const existingWallet = this.subscribedWallets.find(
            ({ wallet: { address } }) => address === control.value
          );
          
          if (existingWallet) {
            switch (existingWallet.wallet.walletType) {
              case 'USER_PLAN':
                return {
                  walletWasAddedToMonitoring: true,
                }
              case 'AML_ENERGY':
                return {
                  walletWasAdded: true,
                }
            }
          }
          return null;
        }
      ],
      [addressValidator(this._apiService, this.translateService)]
    )
  });

  private readonly _router: Router = inject(Router);

  private readonly _dialogService: DialogService = inject(DialogService);

  private readonly _translateService: TranslateService = inject(TranslateService);
  currentLang = this._translateService.currentLang;

  constructor() {
    const { controls: { address } } = this.form;
    const { statusChanges, valueChanges } = address;
    merge(statusChanges, valueChanges)
      .pipe(takeUntilDestroyed())
      .subscribe(() => {
        this.updateAddressErrorMessage();
      });

    // TODO пока что сказали скрыть
    // Вызов диалога для кошельков которые не явщяются TRC-20 адресами
    // statusChanges.pipe(debounceTime(2000), takeUntilDestroyed())
    //   .subscribe(() => {
    //     if (address.hasError('invalidTronAddress')) {
    //       this.openBalanceDialog();
    //     }
    //   });
  }

  ngAfterViewInit() {
    setTimeout(() => {
      this.addressInput?.nativeElement.focus();
    }, 50);
  }

  openBalanceDialog(): void {
    this.projectSettingsService.load();
    this._dialogService.openBalanceDialog().afterClosed().subscribe({
      next: () => {
        this.variablesService.triggerBannersReload();
      }
    });
  }


  updateAddressErrorMessage(): void {
    const { controls: { address } } = this.form;
    let message = '';
    switch (true) {
      case address.hasError('required'): {
        message = 'common.errors.error2';
        break;
      }
      case (address.hasError('invalidTronAddress')): {
        message = 'common.errors.error3';
        break;
      }
      case address.hasError('calculateRentalPlanForWalletError'): {
        message = address.errors?.['calculateRentalPlanForWalletError'];
        break;
      }
      case address.hasError('apiError'): {
        message = address.errors?.['apiError'];
        break;
      }
      case address.hasError('walletWasAdded'): {
        message = 'common.errors.error4';
        break;
      }
      case address.hasError('walletWasAddedToMonitoring'): {
        message = 'common.errors.error41';
        break;
      }
    }

    this.addressErrorMessage.set(message);
  }

  submit(): void {
    // Проверка на изменения формы
    if (!this.form.dirty) {
      this.matDialogRef.close(false);
      return;
    }

    const { wallet: { uuid } } = this.data;
    const body = this.form.getRawValue();
    this._apiService.patchUpdateWallet(uuid, body).pipe(take(1)).subscribe({
      next: async (): Promise<void> => {
        await this._router.navigate(['/subscribed-wallets']);
        this.matDialogRef.close(true);
      },
      error: ({ error }) => {
        let message = this.translateService.instant('common.errors.error5');
        switch (error.code) {
          case 1:
            message = this.translateService.instant('common.errors.error24');
            break;
          case 3: {
            message = this.translateService.instant('common.errors.error10', { contact: contactAsBot });
            break;
          }
          case 5: {
            message = this.translateService.instant('common.errors.error13', { contact: contactAsBot });
            break;
          }
          case 6: {
            message = this.translateService.instant('common.errors.error13', { contact: contactAsBot });
            break;
          }
          case 10:
            message = this.translateService.instant('common.errors.error21');
            break;
          case 11:
            message = this.translateService.instant('common.errors.error22');
            break;
          case 18:
            message = this.translateService.instant('common.errors.error23');
            break;
        }
        this.form.controls.address.setErrors({ apiError: message });
      }
    });
  }

  enableForm(): void {
    this.form.enable({ emitEvent: false });
    this.form.controls.address.reset();
    setTimeout(() => {
      this.addressInput?.nativeElement.focus();
    }, 50);
  }
}
